import Breadcrumbs from "components/Common/Breadcrumb";
import Notifications from "components/Notifications";
import DeleteConfirmationModel from "components/delete-confirmation-model";
import { get, post } from 'helpers/api_helper';
import { getCode } from 'helpers/basic_helper';
import { ROLE_PERMISSION, ROLE_PERMISSION_GET, ROLE_PERMISSION_SAVE } from 'helpers/url_helper';
import React, { useEffect, useState, useCallback } from "react";
import MetaTags from "react-meta-tags";
import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
    Spinner,
    Input
} from "reactstrap";
import ToggleSwitch from '../../../components/Common/ToggleSwitch';
import { GetHelper, SubmitHelper } from '../CallHelper';
import FormComponent from "../FormComponent";

const isType = 'roles';

const Role = () => {
    const [open, isOpen] = useState(false);
    const [editableObj, setEditableObj] = useState(null);
    const [dataRows, setDataRows] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleteModel, setIsDeleteModel] = useState(false);
    const [basePermissions, setBasePermissions] = useState(null);
    const [selectRole, setSelectRole] = useState(false);
    const [checkedState, setCheckedState] = useState({});
    const [isSavingPermissions, setIsSavingPermissions] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleSearch, setRoleSearch] = useState('');
    const [expandedModules, setExpandedModules] = useState({});

    const handleChildCheckboxChange = (permission, subPermission, type) => {
        setCheckedState((prevState) => {
            let updatedSubPermissionState = {
                ...prevState?.[permission]?.[subPermission],
                [type]: !prevState?.[permission]?.[subPermission]?.[type]
            };

            if (type === 'all') {
                const newValue = !prevState?.[permission]?.[subPermission]?.all;
                updatedSubPermissionState = {
                    ...prevState?.[permission]?.[subPermission],
                    all: newValue,
                    read: newValue,
                    add: newValue,
                    edit: newValue,
                    delete: newValue
                };
            }

            return {
                ...prevState,
                [permission]: {
                    ...prevState?.[permission],
                    [subPermission]: updatedSubPermissionState,
                }
            };
        });
    };

    const handleModuleCheckboxChange = (permission, type) => {
        if (!basePermissions || !basePermissions[permission]) return;
        setCheckedState((prevState) => {
            const updatedModuleState = { ...prevState?.[permission] };
            const subPermissions = Object.keys(basePermissions[permission] || {});
            const allChecked = subPermissions.every(M => prevState?.[permission]?.[M]?.[type]);
            const newValue = !allChecked;

            subPermissions.forEach(subPermission => {
                if (type === 'all') {
                    updatedModuleState[subPermission] = {
                        ...updatedModuleState[subPermission],
                        all: newValue,
                        read: newValue,
                        add: newValue,
                        edit: newValue,
                        delete: newValue
                    };
                } else {
                    updatedModuleState[subPermission] = {
                        ...updatedModuleState[subPermission],
                        [type]: newValue
                    };
                }
            });

            return {
                ...prevState,
                [permission]: updatedModuleState
            };
        });
    };

    const handleGlobalCheckboxChange = (type) => {
        if (!basePermissions) return;
        setCheckedState((prevState) => {
            const newState = { ...prevState };
            const allItems = [];
            Object.keys(basePermissions).forEach(v => {
                Object.keys(basePermissions[v]).forEach(M => {
                    allItems.push({ v, M });
                });
            });

            const allChecked = allItems.every(item => prevState?.[item.v]?.[item.M]?.[type]);
            const newValue = !allChecked;

            allItems.forEach(item => {
                if (!newState[item.v]) newState[item.v] = {};
                if (type === 'all') {
                    newState[item.v][item.M] = {
                        ...newState[item.v][item.M],
                        all: newValue,
                        read: newValue,
                        add: newValue,
                        edit: newValue,
                        delete: newValue
                    };
                } else {
                    newState[item.v][item.M] = {
                        ...newState[item.v][item.M],
                        [type]: newValue
                    };
                }
            });

            return newState;
        });
    };

    const toggleModule = (module) => {
        setExpandedModules(prev => ({
            ...prev,
            [module]: !prev[module]
        }));
    };

    const toggleAllModules = (expand = true) => {
        if (!basePermissions) return;
        const newState = {};
        Object.keys(basePermissions).forEach(v => {
            newState[v] = expand;
        });
        setExpandedModules(newState);
    };

    const getBasePermissions = async () => {
        try {
            const res = await get(`${ROLE_PERMISSION}`);
            if (res.status === 200) {
                setBasePermissions(res.response);
            }
        } catch (e) {
            console.error("Failed to load base permissions", e);
        }
    };

    const getData = useCallback(async (ID = false) => {
        if (!ID) setIsLoading(true);
        try {
            let response = await GetHelper(`${process.env.REACT_APP_ROLE}`, ID);
            if (ID) {
                const singleObj = response?.rows?.[0] || response?.[0] || response;
                if (singleObj) {
                    setEditableObj(singleObj);
                    isOpen(true);
                }
            } else {
                let list = response?.rows ? response.rows : (Array.isArray(response) ? response : []);
                list = list.filter(v => v.title !== 'Superadmin');
                setDataRows({ rows: list, total_rows: list.length });
                if (!selectRole && list.length > 0) {
                    setSelectRole(list[0].code);
                }
            }
        } catch (err) {
            console.error("Error fetching roles", err);
        } finally {
            setIsLoading(false);
        }

        getBasePermissions();
    }, [selectRole]);

    useEffect(() => {
        getData();
    }, []);

    const submitForm = async (e, formValues) => {
        const titleVal = formValues?.title || e?.target?.title?.value;
        const codeVal = formValues?.code || e?.target?.code?.value;
        const idVal = formValues?.id || editableObj?.id || null;

        if (!titleVal || !codeVal) {
            Notifications('Please enter role title and code!', 'error');
            return;
        }

        const checkCode = dataRows?.rows?.filter(v => (v.code || "").toUpperCase() === (codeVal || "").toUpperCase() && String(v.id) !== String(idVal));
        if (checkCode && checkCode.length > 0) {
            Notifications('Role code already exists. Please choose a unique code!', 'error');
            return;
        }

        const res = await SubmitHelper(`${process.env.REACT_APP_ROLE}`, {
            "title": titleVal,
            "code": codeVal
        }, idVal);

        if (res) {
            setEditableObj(null);
            isOpen(false);
            getData();
            Notifications('Role saved successfully!', 'success');
        }
    };

    const handleEdit = (q) => {
        let target = null;
        if (typeof q === 'object' && q !== null) {
            target = q;
        } else {
            target = dataRows?.rows?.find(r => String(r.id) === String(q));
        }

        if (target) {
            setEditableObj({ ...target });
            isOpen(true);
        } else if (q > 0) {
            getData(q);
        }
    };

    const handleRemove = async (q, status, code) => {
        if (q > 0) {
            setIsDeleteModel({ url: process.env.REACT_APP_ROLE, q, status, code });
        }
    };

    const hideModel = () => {
        setIsDeleteModel(false);
        getData();
    };

    let htmlFormFields = [
        {
            name: "id",
            required: false,
            type: 'hidden',
            value: editableObj?.id || '',
            col: 12
        },
        {
            name: "title",
            label: "Role Title",
            placeholder: "e.g. Senior Technician / Accounts Head",
            required: true,
            type: 'text',
            value: editableObj?.title || '',
            col: 12
        },
        {
            name: "code",
            label: "Role Code",
            placeholder: "e.g. TECH / ACCT",
            required: true,
            type: 'text',
            value: editableObj?.code || getCode("R", dataRows?.total_rows || dataRows?.rows?.length || 1),
            col: 12
        }
    ];

    const submitPermissions = async () => {
        if (!selectRole) {
            Notifications('Please select a user role first!', 'warning');
            return;
        }

        setIsSavingPermissions(true);
        try {
            const Obj = {
                role: selectRole,
                permissions: JSON.stringify(checkedState || {})
            };

            const res = await post(ROLE_PERMISSION_SAVE, Obj);
            if (res.status === 200) {
                const currentRoleTitle = dataRows?.rows?.find(r => r.code === selectRole)?.title || selectRole;
                Notifications(`Permissions for "${currentRoleTitle}" saved successfully!`, 'success');
            } else {
                Notifications(res.message || 'Failed to save permissions.', 'error');
            }
        } catch (err) {
            Notifications('Error occurred while saving permissions.', 'error');
        } finally {
            setIsSavingPermissions(false);
        }
    };

    useEffect(() => {
        if (selectRole) {
            const fetchRolePermissions = async () => {
                try {
                    const res = await post(ROLE_PERMISSION_GET, { role: selectRole });
                    if (res.status === 200) {
                        const data = res?.response ? JSON.parse(res?.response?.permissions) : {};
                        if (data) setCheckedState(data);
                    }
                } catch (e) {
                    console.error("Error loading role permissions", e);
                }
            };
            fetchRolePermissions();
        }
    }, [selectRole]);

    const selectedRoleObj = dataRows?.rows?.find(r => r.code === selectRole);
    const filteredRoleRows = (dataRows?.rows || []).filter(r =>
        (r.title || "").toLowerCase().includes(roleSearch.toLowerCase()) ||
        (r.code || "").toLowerCase().includes(roleSearch.toLowerCase())
    );

    const renderPermissionTable = () => {
        const rows = [];
        if (!basePermissions) return null;

        Object.keys(basePermissions).forEach((v, i) => {
            const subModules = Object.keys(basePermissions[v]);
            const filteredSubModules = subModules.filter(M =>
                M.toLowerCase().includes(searchTerm.toLowerCase()) ||
                v.toLowerCase().includes(searchTerm.toLowerCase())
            );

            if (filteredSubModules.length === 0) return;
            const isExpanded = expandedModules[v] !== false;
            const isReports = v === 'reports';

            // Module Group Header Row
            rows.push(
                <tr
                    key={`MH_${i}`}
                    style={{
                        background: isReports ? '#f0fdf4' : '#f1f5f9',
                        borderBottom: '2px solid #cbd5e1',
                        cursor: 'pointer'
                    }}
                >
                    <td style={{ width: '40px', textAlign: 'center' }} onClick={() => toggleModule(v)}>
                        <i className={`fas ${isExpanded ? 'fa-chevron-down text-primary' : 'fa-chevron-right text-muted'}`}></i>
                    </td>
                    <td onClick={() => toggleModule(v)} style={{ fontWeight: '700', fontSize: '14px', color: '#0f172a' }}>
                        <i className={`fas ${isReports ? 'fa-chart-bar text-success' : 'fa-folder-open text-primary'} me-2`}></i>
                        {v?.toUpperCase().replace(/_/g, ' ')}
                        <span
                            className="ms-2 badge"
                            style={{ background: '#e2e8f0', color: '#1e293b', fontSize: '11px', fontWeight: '700' }}
                        >
                            {subModules.length} submodules
                        </span>
                        {isReports && (
                            <span className="ms-2 badge bg-success" style={{ fontSize: '10px' }}>Read / Download Only</span>
                        )}
                    </td>

                    {/* ALL — hidden for reports */}
                    <td className="text-center" style={{ background: '#eff6ff' }}>
                        {!isReports ? (
                            <ToggleSwitch
                                checked={subModules.every(M => checkedState?.[v]?.[M]?.all)}
                                onChange={() => handleModuleCheckboxChange(v, 'all')}
                            />
                        ) : <span className="text-muted">—</span>}
                    </td>

                    {/* READ */}
                    <td className="text-center">
                        <ToggleSwitch
                            checked={subModules.every(M => checkedState?.[v]?.[M]?.read)}
                            onChange={() => handleModuleCheckboxChange(v, 'read')}
                        />
                    </td>

                    {/* ADD — hidden for reports, shows Download toggle instead */}
                    <td className="text-center">
                        {!isReports ? (
                            <ToggleSwitch
                                checked={subModules.every(M => checkedState?.[v]?.[M]?.add)}
                                onChange={() => handleModuleCheckboxChange(v, 'add')}
                            />
                        ) : (
                            <ToggleSwitch
                                checked={subModules.every(M => checkedState?.[v]?.[M]?.download)}
                                onChange={() => handleModuleCheckboxChange(v, 'download')}
                            />
                        )}
                    </td>

                    {/* EDIT — hidden for reports */}
                    <td className="text-center">
                        {!isReports ? (
                            <ToggleSwitch
                                checked={subModules.every(M => checkedState?.[v]?.[M]?.edit)}
                                onChange={() => handleModuleCheckboxChange(v, 'edit')}
                            />
                        ) : <span className="text-muted">—</span>}
                    </td>

                    {/* DEL — hidden for reports */}
                    <td className="text-center">
                        {!isReports ? (
                            <ToggleSwitch
                                checked={subModules.every(M => checkedState?.[v]?.[M]?.delete)}
                                onChange={() => handleModuleCheckboxChange(v, 'delete')}
                            />
                        ) : <span className="text-muted">—</span>}
                    </td>

                    <td className="text-center" style={{ color: '#64748b', fontSize: '12px', fontWeight: '600' }}>
                        {isReports ? (
                            <span className="text-success"><i className="fas fa-eye me-1"></i>View Only</span>
                        ) : 'Category Master'}
                    </td>
                </tr>
            );

            // Submodule Detail Rows
            if (isExpanded) {
                filteredSubModules.forEach((M, I) => {
                    rows.push(
                        <tr key={`RP_${i + 1}_${I + 1}`} className="bg-white border-bottom">
                            <td style={{ width: '40px' }}></td>
                            <td style={{ paddingLeft: '32px', color: '#1e293b', fontWeight: '600', fontSize: '13px' }}>
                                <i className={`fas ${isReports ? 'fa-file-alt text-success' : 'fa-angle-right text-muted'} me-2`}></i>
                                {M?.toUpperCase().replace(/_/g, ' ')}
                            </td>

                            {/* ALL — hidden for reports */}
                            <td className="text-center" style={{ background: '#f8fafc' }}>
                                {!isReports ? (
                                    <ToggleSwitch
                                        checked={checkedState?.[v]?.[M]?.all || false}
                                        onChange={() => handleChildCheckboxChange(v, M, 'all')}
                                    />
                                ) : <span className="text-muted">—</span>}
                            </td>

                            {/* READ */}
                            <td className="text-center">
                                <ToggleSwitch
                                    checked={checkedState?.[v]?.[M]?.read || false}
                                    onChange={() => handleChildCheckboxChange(v, M, 'read')}
                                />
                            </td>

                            {/* ADD — shows Download for reports */}
                            <td className="text-center">
                                {!isReports ? (
                                    <ToggleSwitch
                                        checked={checkedState?.[v]?.[M]?.add || false}
                                        onChange={() => handleChildCheckboxChange(v, M, 'add')}
                                    />
                                ) : (
                                    <ToggleSwitch
                                        label=""
                                        checked={checkedState?.[v]?.[M]?.download || false}
                                        onChange={() => handleChildCheckboxChange(v, M, 'download')}
                                    />
                                )}
                            </td>

                            {/* EDIT — hidden for reports */}
                            <td className="text-center">
                                {!isReports ? (
                                    <ToggleSwitch
                                        checked={checkedState?.[v]?.[M]?.edit || false}
                                        onChange={() => handleChildCheckboxChange(v, M, 'edit')}
                                    />
                                ) : <span className="text-muted">—</span>}
                            </td>

                            {/* DEL — hidden for reports */}
                            <td className="text-center">
                                {!isReports ? (
                                    <ToggleSwitch
                                        checked={checkedState?.[v]?.[M]?.delete || false}
                                        onChange={() => handleChildCheckboxChange(v, M, 'delete')}
                                    />
                                ) : <span className="text-muted">—</span>}
                            </td>

                            {/* Special Actions column */}
                            <td>
                                <div className="d-flex flex-wrap gap-2">
                                    {isReports && (
                                        <span className="badge" style={{ background: '#dcfce7', color: '#166534', fontSize: '11px' }}>
                                            <i className="fas fa-download me-1"></i>Download Only
                                        </span>
                                    )}
                                    {M === 'client' && (
                                        <>
                                            <ToggleSwitch
                                                label="Blocking"
                                                checked={checkedState?.[v]?.[M]?.cb || false}
                                                onChange={() => handleChildCheckboxChange(v, M, 'cb')}
                                            />
                                            <ToggleSwitch
                                                label="Discount"
                                                checked={checkedState?.[v]?.[M]?.pd || false}
                                                onChange={() => handleChildCheckboxChange(v, M, 'pd')}
                                            />
                                        </>
                                    )}
                                    {M === 'blocked_client' && (
                                        <ToggleSwitch
                                            label="Un-Blocking"
                                            checked={checkedState?.[v]?.[M]?.cub || false}
                                            onChange={() => handleChildCheckboxChange(v, M, 'cub')}
                                        />
                                    )}
                                    {M === 'orders' && (
                                        <>
                                            <ToggleSwitch
                                                label="Redo"
                                                checked={checkedState?.[v]?.[M]?.ro || false}
                                                onChange={() => handleChildCheckboxChange(v, M, 'ro')}
                                            />
                                            <ToggleSwitch
                                                label="Correction"
                                                checked={checkedState?.[v]?.[M]?.co || false}
                                                onChange={() => handleChildCheckboxChange(v, M, 'co')}
                                            />
                                            <ToggleSwitch
                                                label="JobCard"
                                                checked={checkedState?.[v]?.[M]?.jobcard || false}
                                                onChange={() => handleChildCheckboxChange(v, M, 'jobcard')}
                                            />
                                            <ToggleSwitch
                                                label="LabSlip"
                                                checked={checkedState?.[v]?.[M]?.ls || false}
                                                onChange={() => handleChildCheckboxChange(v, M, 'ls')}
                                            />
                                        </>
                                    )}
                                </div>
                            </td>
                        </tr>
                    );
                });
            }
        });

        return rows;
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags><title>Roles & Permissions Matrix | DG Dental Lab</title></MetaTags>
                <Container fluid>
                    <Breadcrumbs title="Masters" breadcrumbItem="Roles & Permissions Matrix" />
                    <Row className="g-3">
                        {/* Left Column: Roles Management */}
                        <Col lg="3" md="4">
                            <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '12px' }}>
                                <CardBody className="p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h5 className="m-0 fw-bold text-dark font-size-15 d-flex align-items-center">
                                            <i className="mdi mdi-account-group-outline text-primary me-2 font-size-18"></i>
                                            User Roles
                                        </h5>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary d-inline-flex align-items-center"
                                            style={{ borderRadius: '6px', fontWeight: '600' }}
                                            onClick={() => {
                                                setEditableObj(null);
                                                isOpen(true);
                                            }}
                                        >
                                            <i className="bx bx-plus me-1 font-size-14"></i> Add
                                        </button>
                                    </div>

                                    {/* Role Search */}
                                    <div className="position-relative mb-3">
                                        <Input
                                            type="text"
                                            className="form-control form-control-sm rounded-pill ps-4 bg-light border-light"
                                            placeholder="Search roles..."
                                            value={roleSearch}
                                            onChange={(e) => setRoleSearch(e.target.value)}
                                        />
                                        <i className="fas fa-search position-absolute text-muted" style={{ left: '12px', top: '9px', fontSize: '11px' }}></i>
                                    </div>

                                    {/* Role List */}
                                    {isLoading ? (
                                        <div className="text-center py-4">
                                            <Spinner size="sm" color="primary" />
                                            <div className="mt-2 text-muted font-size-12">Loading roles...</div>
                                        </div>
                                    ) : (
                                        <div className="list-group list-group-flush" style={{ maxHeight: '68vh', overflowY: 'auto' }}>
                                            {filteredRoleRows.map((role) => {
                                                const isSelected = selectRole === role.code;
                                                return (
                                                    <div
                                                        key={role.id}
                                                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center px-3 py-2 mb-1"
                                                        style={{
                                                            cursor: 'pointer',
                                                            borderRadius: '8px',
                                                            border: isSelected ? '1px solid #93c5fd' : '1px solid #f1f5f9',
                                                            background: isSelected ? '#eff6ff' : '#ffffff',
                                                            transition: 'all 0.15s ease'
                                                        }}
                                                        onClick={() => setSelectRole(role.code)}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            <span
                                                                className="badge rounded-circle me-2 d-flex align-items-center justify-content-center"
                                                                style={{
                                                                    width: '28px',
                                                                    height: '28px',
                                                                    background: isSelected ? '#2563eb' : '#e2e8f0',
                                                                    color: isSelected ? '#ffffff' : '#475569',
                                                                    fontSize: '11px'
                                                                }}
                                                            >
                                                                #{role.id}
                                                            </span>
                                                            <div>
                                                                <div style={{
                                                                    fontWeight: isSelected ? '700' : '600',
                                                                    color: isSelected ? '#1d4ed8' : '#0f172a',
                                                                    fontSize: '13px'
                                                                }}>
                                                                    {role.title}
                                                                </div>
                                                                <small style={{ color: '#64748b', fontSize: '11px' }}>Code: {role.code}</small>
                                                            </div>
                                                        </div>
                                                        <div className="d-flex align-items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm"
                                                                style={{ background: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1', padding: '4px 8px', borderRadius: '4px' }}
                                                                onClick={() => handleEdit(role)}
                                                                title="Edit Role"
                                                            >
                                                                <i className="fas fa-pencil-alt" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm"
                                                                style={{ background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', padding: '4px 8px', borderRadius: '4px' }}
                                                                onClick={() => handleRemove(role.id, role.status, role.code)}
                                                                title="Delete Role"
                                                            >
                                                                <i className="fas fa-trash-alt" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {filteredRoleRows.length === 0 && (
                                                <div className="text-center py-4 text-muted font-size-13">
                                                    No roles found matching &quot;{roleSearch}&quot;
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>

                        {/* Right Column: Permissions Matrix */}
                        <Col lg="9" md="8">
                            <Card className="shadow-sm border-0" style={{ borderRadius: '12px' }}>
                                <CardBody className="p-3">
                                    {/* Top Controls Bar */}
                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 pb-3 border-bottom mb-3">
                                        <div>
                                            <div className="d-flex align-items-center gap-2">
                                                <h4 className="m-0 fw-bold text-dark font-size-18">
                                                    Permissions Matrix
                                                </h4>
                                                {selectedRoleObj && (
                                                    <span
                                                        style={{
                                                            background: '#dbeafe',
                                                            color: '#1d4ed8',
                                                            border: '1px solid #bfdbfe',
                                                            padding: '3px 10px',
                                                            borderRadius: '20px',
                                                            fontSize: '12px',
                                                            fontWeight: '700'
                                                        }}
                                                    >
                                                        {selectedRoleObj.title} ({selectedRoleObj.code})
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-muted mb-0 font-size-13 mt-1">
                                                Configure access levels, CRUD actions, and specialized modules for this role.
                                            </p>
                                        </div>

                                        <div className="d-flex align-items-center gap-2">
                                            {/* Role Selector Dropdown */}
                                            <div style={{ minWidth: '180px' }}>
                                                <select
                                                    className="form-select form-select-sm fw-semibold"
                                                    value={selectRole || ""}
                                                    onChange={(e) => setSelectRole(e.target.value)}
                                                    style={{ borderColor: '#cbd5e1', borderRadius: '6px' }}
                                                >
                                                    <option value="">-- Choose Role --</option>
                                                    {dataRows?.rows?.map((v, i) => (
                                                        <option key={`ROP_${i}`} value={v.code}>{v.title}</option>
                                                    ))}
                                                </select>
                                            </div>

                                            {/* Save Button */}
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-primary d-inline-flex align-items-center px-3"
                                                style={{
                                                    background: '#2563eb',
                                                    border: 'none',
                                                    fontWeight: '700',
                                                    borderRadius: '6px',
                                                    boxShadow: '0 2px 6px rgba(37,99,235,0.3)'
                                                }}
                                                disabled={!selectRole || isSavingPermissions}
                                                onClick={submitPermissions}
                                            >
                                                {isSavingPermissions ? (
                                                    <><Spinner size="sm" className="me-1" /> Saving...</>
                                                ) : (
                                                    <><i className="bx bx-save me-1 font-size-15 align-middle"></i> Save Permissions</>
                                                )}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Sub-toolbar: Search & Expand Controls */}
                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                                        <div className="position-relative" style={{ minWidth: '260px' }}>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm rounded-pill bg-light border-light ps-4"
                                                placeholder="Search modules or submodules..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                            <i className="fas fa-search position-absolute text-muted" style={{ left: '12px', top: '9px', fontSize: '12px' }}></i>
                                        </div>

                                        <div className="d-flex gap-1">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-light text-dark border"
                                                onClick={() => toggleAllModules(true)}
                                                title="Expand All Modules"
                                            >
                                                <i className="fas fa-expand-arrows-alt me-1 font-size-11"></i> Expand All
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-light text-dark border"
                                                onClick={() => toggleAllModules(false)}
                                                title="Collapse All Modules"
                                            >
                                                <i className="fas fa-compress-arrows-alt me-1 font-size-11"></i> Collapse All
                                            </button>
                                        </div>
                                    </div>

                                    {/* Permissions Matrix Table Container */}
                                    <div style={{
                                        maxHeight: '62vh',
                                        overflowY: 'auto',
                                        borderRadius: '10px',
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                    }}>
                                        <table className="table table-sm mb-0 align-middle">
                                            <thead style={{
                                                position: 'sticky',
                                                top: 0,
                                                background: '#f8fafc',
                                                zIndex: 10,
                                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                            }}>
                                                <tr>
                                                    <th style={{ width: '36px', textAlign: 'center', background: '#f8fafc', color: '#64748b', fontWeight: '700', fontSize: '11px', borderBottom: '1px solid #cbd5e1', verticalAlign: 'middle', padding: '6px 4px' }}>#</th>
                                                    <th style={{ minWidth: '200px', background: '#f8fafc', color: '#1e293b', fontWeight: '700', fontSize: '12px', borderBottom: '1px solid #cbd5e1', verticalAlign: 'middle', padding: '6px 8px' }}>Module / Submodule</th>
                                                    <th className="text-center" style={{ width: '80px', background: '#f8fafc', borderBottom: '1px solid #cbd5e1', padding: '6px 2px' }}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <span style={{ fontSize: '10px', fontWeight: '800', color: '#3730a3', letterSpacing: '0.5px', marginBottom: '2px' }}>ALL</span>
                                                            <ToggleSwitch
                                                                checked={basePermissions && Object.keys(basePermissions).every(v => Object.keys(basePermissions[v]).every(M => checkedState?.[v]?.[M]?.all))}
                                                                onChange={() => handleGlobalCheckboxChange('all')}
                                                            />
                                                        </div>
                                                    </th>
                                                    <th className="text-center" style={{ width: '80px', background: '#f8fafc', borderBottom: '1px solid #cbd5e1', padding: '6px 2px' }}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <span style={{ fontSize: '10px', fontWeight: '800', color: '#0369a1', letterSpacing: '0.5px', marginBottom: '2px' }}>READ</span>
                                                            <ToggleSwitch
                                                                checked={basePermissions && Object.keys(basePermissions).every(v => Object.keys(basePermissions[v]).every(M => checkedState?.[v]?.[M]?.read))}
                                                                onChange={() => handleGlobalCheckboxChange('read')}
                                                            />
                                                        </div>
                                                    </th>
                                                    <th className="text-center" style={{ width: '80px', background: '#f8fafc', borderBottom: '1px solid #cbd5e1', padding: '6px 2px' }}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <span style={{ fontSize: '10px', fontWeight: '800', color: '#15803d', letterSpacing: '0.5px', marginBottom: '2px' }}>ADD</span>
                                                            <span style={{ fontSize: '9px', color: '#16a34a', marginBottom: '2px', fontStyle: 'italic' }}>/ DL</span>
                                                            <ToggleSwitch
                                                                checked={basePermissions && Object.keys(basePermissions).every(v => Object.keys(basePermissions[v]).every(M => checkedState?.[v]?.[M]?.add))}
                                                                onChange={() => handleGlobalCheckboxChange('add')}
                                                            />
                                                        </div>
                                                    </th>
                                                    <th className="text-center" style={{ width: '80px', background: '#f8fafc', borderBottom: '1px solid #cbd5e1', padding: '6px 2px' }}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <span style={{ fontSize: '10px', fontWeight: '800', color: '#b45309', letterSpacing: '0.5px', marginBottom: '2px' }}>EDIT</span>
                                                            <ToggleSwitch
                                                                checked={basePermissions && Object.keys(basePermissions).every(v => Object.keys(basePermissions[v]).every(M => checkedState?.[v]?.[M]?.edit))}
                                                                onChange={() => handleGlobalCheckboxChange('edit')}
                                                            />
                                                        </div>
                                                    </th>
                                                    <th className="text-center" style={{ width: '80px', background: '#f8fafc', borderBottom: '1px solid #cbd5e1', padding: '6px 2px' }}>
                                                        <div className="d-flex flex-column align-items-center">
                                                            <span style={{ fontSize: '10px', fontWeight: '800', color: '#b91c1c', letterSpacing: '0.5px', marginBottom: '2px' }}>DEL</span>
                                                            <ToggleSwitch
                                                                checked={basePermissions && Object.keys(basePermissions).every(v => Object.keys(basePermissions[v]).every(M => checkedState?.[v]?.[M]?.delete))}
                                                                onChange={() => handleGlobalCheckboxChange('delete')}
                                                            />
                                                        </div>
                                                    </th>
                                                    <th style={{ minWidth: '180px', background: '#f8fafc', color: '#1e293b', fontWeight: '700', fontSize: '12px', borderBottom: '1px solid #cbd5e1', verticalAlign: 'middle', padding: '6px 8px' }}>Special Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {basePermissions && renderPermissionTable()}
                                                {!basePermissions && (
                                                    <tr>
                                                        <td colSpan={8} className="text-center py-5">
                                                            <Spinner color="primary" />
                                                            <div className="mt-2 text-muted">Loading permissions matrix...</div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Bottom Sticky Action Footer */}
                                    {basePermissions && (
                                        <div className="p-3 text-center bg-white border-top mt-3 d-flex justify-content-between align-items-center">
                                            <div className="text-muted font-size-13">
                                                {selectedRoleObj ? (
                                                    <span>Target Role: <strong className="text-dark">{selectedRoleObj.title}</strong></span>
                                                ) : (
                                                    <span className="text-warning">Select a role above to save permissions</span>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-primary px-4 fw-bold shadow-sm"
                                                style={{ minWidth: '220px', borderRadius: '8px' }}
                                                onClick={submitPermissions}
                                                disabled={!selectRole || isSavingPermissions}
                                            >
                                                {isSavingPermissions ? (
                                                    <><Spinner size="sm" className="me-2" /> Saving Changes...</>
                                                ) : (
                                                    <><i className="fas fa-check-circle me-2"></i> Save All Permissions</>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* Add / Edit Role Drawer */}
            {open && (
                <FormComponent
                    title={`${editableObj ? 'Edit' : 'Add'} Role`}
                    Position={"right"}
                    isOpen={open}
                    width={28}
                    fileds={htmlFormFields}
                    loading={isLoading}
                    editableObj={editableObj}
                    onDrawerClose={() => {
                        setEditableObj(null);
                        isOpen(false);
                    }}
                    submitForm={submitForm}
                />
            )}

            {isDeleteModel && <DeleteConfirmationModel isOpen={true} changeState={hideModel} code={isDeleteModel} />}
        </React.Fragment>
    );
};

export default Role;
